import NodeIds from './NodeIds';
import InputIds from './InputIds';
import { InputMetadataEntry } from '../types';

type InputMetadataType = {
    [NodeId in keyof typeof InputIds]: {
        [InputId in keyof (typeof InputIds)[NodeId]]: InputMetadataEntry;
    };
};

const InputMetadata: InputMetadataType = {
    [NodeIds.GeneralInformation]: {
        [InputIds.GeneralInformation.BusinessId]: {
            name: 'Business ID'
        },
        [InputIds.GeneralInformation.ExpiringPeriodStart]: {
            name: 'Expiring period start'
        },
        [InputIds.GeneralInformation.MainClass]: {
            name: 'Main class'
        },
        [InputIds.GeneralInformation.Class]: {
            name: 'Class(es)'
        },
        [InputIds.GeneralInformation.SubClass]: {
            name: 'Sub class(es)'
        },
        [InputIds.GeneralInformation.CountryGroups]: {
            name: 'Country group'
        },
        [InputIds.GeneralInformation.Countries]: {
            name: 'Countries'
        },
        [InputIds.GeneralInformation.Title]: {
            name: 'Contract name'
        },
        [InputIds.GeneralInformation.CreateProgram]: {
            name: 'Create program'
        },
        [InputIds.GeneralInformation.TypeOfParticipation]: {
            name: 'Type of participation'
        },
        [InputIds.GeneralInformation.BusinessType]: {
            name: 'Business type'
        },
        [InputIds.GeneralInformation.UnderwritingYear]: {
            name: 'Underwriting year'
        },
        [InputIds.GeneralInformation.PeriodStart]: {
            name: 'Period start'
        },
        [InputIds.GeneralInformation.PeriodEnd]: {
            name: 'Period end'
        },
        [InputIds.GeneralInformation.Lifecycle]: {
            name: 'Lifecycle'
        },
        [InputIds.GeneralInformation.Currency]: {
            name: 'Currency'
        },
        [InputIds.GeneralInformation.Cedent]: {
            name: 'Cedent'
        },
        [InputIds.GeneralInformation.Insured]: {
            name: 'Insured'
        },
        [InputIds.GeneralInformation.Broker]: {
            name: 'Broker'
        },
        [InputIds.GeneralInformation.Reinsurer]: {
            name: 'Reinsurer'
        },
        [InputIds.GeneralInformation.ReportUnits]: {
            name: 'Reporting unit'
        }
    },
    [NodeIds.Classifications]: {
        [InputIds.Classifications.MainClass]: {
            name: 'Main class'
        },
        [InputIds.Classifications.Class]: {
            name: 'Class(es)'
        },
        [InputIds.Classifications.SubClass]: {
            name: 'Sub class(es)'
        }
    },
    [NodeIds.Countries]: {
        [InputIds.Countries.CountryGroups]: {
            name: 'Country group'
        },
        [InputIds.Countries.Countries]: {
            name: 'Countries'
        }
    },
    [NodeIds.Limits]: {
        [InputIds.Limits.Cover]: {
            name: 'Cover'
        },
        [InputIds.Limits.Excess]: {
            name: 'Excess'
        },
        [InputIds.Limits.Liability]: {
            name: 'Liability'
        },
        [InputIds.Limits.MainLimit]: {
            name: 'Main Limit'
        },
        [InputIds.Limits.TotalSumInsured]: {
            name: 'Total Sum Insured'
        },
        [InputIds.Limits.LimitType]: {
            name: 'Limits Type'
        },
        [InputIds.Limits.LimitValue]: {
            name: 'Limits'
        }
    },
    [NodeIds.Premiums]: {
        [InputIds.Premiums.PremiumRate]: {
            name: 'Premium Rate %'
        },
        [InputIds.Premiums.PremiumType]: {
            name: 'Premium Type'
        },
        [InputIds.Premiums.EstimatedPremiumIncome]: {
            name: 'Estimated Premium Income'
        },
        [InputIds.Premiums.MinimalPremium]: {
            name: 'Minimum Premium'
        },
        [InputIds.Premiums.DepositPremium]: {
            name: 'Deposit Premium'
        },
        [InputIds.Premiums.SubjectPremiumIncome]: {
            name: 'Subject Premium Income'
        },
        [InputIds.Premiums.TotalGrossPremium]: {
            name: 'Total Gross Premium'
        },
        [InputIds.Premiums.FlatPremium]: {
            name: 'Flat Premium'
        },
        [InputIds.Premiums.AnnualPremium]: {
            name: 'Annual'
        }
    },
    [NodeIds.Deductions]: {
        [InputIds.Deductions.Commission]: {
            name: 'Comission %'
        },
        [InputIds.Deductions.Brokerage]: {
            name: 'Brokerage %'
        },
        [InputIds.Deductions.TaxOnPremium]: {
            name: 'Tax on Premium %'
        },
        [InputIds.Deductions.DeductionType]: {
            name: 'Deduction Type'
        },
        [InputIds.Deductions.DeductionValue]: {
            name: 'Deduction'
        }
    },
    [NodeIds.Share]: {
        [InputIds.Share.ShareOffer]: {
            name: 'Offer %'
        },
        [InputIds.Share.ShareWritten]: {
            name: 'Written %'
        },
        [InputIds.Share.ShareSigned]: {
            name: 'Signed %'
        },
        [InputIds.Share.CessionPercent]: {
            name: 'Cession %'
        }
    },
    [NodeIds.Claims]: {
        [InputIds.Claims.ClaimBasis]: {
            name: 'Claim Basis'
        },
        [InputIds.Claims.ClaimAdviceLimit]: {
            name: 'Claim Advice Limit'
        }
    },
    [NodeIds.PortfolioConditions]: {
        [InputIds.PortfolioConditions.PremiumEntryCalculationMethod]: {
            name: 'Premium Entry Calculation Method'
        },
        [InputIds.PortfolioConditions.PremiumEntryPercent]: {
            name: 'Premium Entry %'
        },
        [InputIds.PortfolioConditions.PremiumEntryPercentOf]: {
            name: 'Premium Entry Percent Of'
        },
        [InputIds.PortfolioConditions.PremiumWithdrawalCalculationMethod]: {
            name: 'Premium Withdrawal Calculation Method'
        },
        [InputIds.PortfolioConditions.PremiumWithdrawalPercent]: {
            name: 'Premium Withdrawal %'
        },
        [InputIds.PortfolioConditions.PremiumWithdrawalPercentOf]: {
            name: 'Premium Withdrawal Percent Of'
        },
        [InputIds.PortfolioConditions.LossEntryCalculationMethod]: {
            name: 'Loss Entry Calculation Method'
        },
        [InputIds.PortfolioConditions.LossEntryPercent]: {
            name: 'Loss Entry %'
        },
        [InputIds.PortfolioConditions.LossWithdrawalCalculationMethod]: {
            name: 'Loss Withdrawal Calculation Method'
        },
        [InputIds.PortfolioConditions.LossWithdrawalPercent]: {
            name: 'Loss Withdrawal %'
        }
    },
    [NodeIds.AdminConditions]: {
        [InputIds.AdminConditions.CessionBasis]: {
            name: 'Cession Basis'
        },
        [InputIds.AdminConditions.CashClaimLimit]: {
            name: 'Cash Claim Limit'
        }
    },
    [NodeIds.OtherConditions]: {
        [InputIds.OtherConditions.CancellationTerms]: {
            name: 'Cancellation Terms'
        },
        [InputIds.OtherConditions.NOCPeriod]: {
            name: 'NOC Period'
        },
        [InputIds.OtherConditions.PremiumPaymentWarranty]: {
            name: 'Premium Payment Warranty'
        },
        [InputIds.OtherConditions.PPWPeriod]: {
            name: 'PPW Period'
        },
        [InputIds.OtherConditions.InclusionClauses]: {
            name: 'Inclusion Clauses'
        },
        [InputIds.OtherConditions.ExclusionClauses]: {
            name: 'Exclusion Clauses'
        }
    },
    [NodeIds.Instalments]: {
        [InputIds.Instalments.InstalmentsCount]: {
            name: 'Number of instalments'
        },
        [InputIds.Instalments.FirstInstalmentCount]: {
            name: 'Payment of first instalment'
        },
        [InputIds.Instalments.FirstInstalmentPeriod]: {
            name: 'Period'
        },
        [InputIds.Instalments.InstalmentType]: {
            name: 'Instalments Type'
        },
        [InputIds.Instalments.SubsequentInstalmentCount]: {
            name: 'Payment of subsequent instalment'
        },
        [InputIds.Instalments.SubsequentInstalmentPeriod]: {
            name: 'Period'
        }
    },
    [NodeIds.Adjustments]: {
        [InputIds.Adjustments.AdjustmentsCount]: {
            name: 'Number of Adjustments'
        },
        [InputIds.Adjustments.FirstPeriodCount]: {
            name: 'First Period'
        },
        [InputIds.Adjustments.FirstPeriodTime]: {
            name: 'Period'
        },
        [InputIds.Adjustments.SubsequentAdjustmentsPeriodCount]: {
            name: 'Subsequent Period'
        },
        [InputIds.Adjustments.SubsequentAdjustmentsPeriodTime]: {
            name: 'Period'
        },
        [InputIds.Adjustments.SubmissionWithinCount]: {
            name: 'Submission Within'
        },
        [InputIds.Adjustments.SubmissionWithinPeriod]: {
            name: 'Period'
        },
        [InputIds.Adjustments.PaymentWithinCount]: {
            name: 'Payment Within'
        },
        [InputIds.Adjustments.PaymentWithinPeriod]: {
            name: 'Period'
        }
    },
    [NodeIds.Reinstatements]: {
        [InputIds.Reinstatements.CurrencyType]: {
            name: 'Currency Type'
        },
        [InputIds.Reinstatements.CalculationRule]: {
            name: 'Calculation Rule'
        },
        [InputIds.Reinstatements.ReinstatementsType]: {
            name: 'Reinstatements'
        },
        [InputIds.Reinstatements.CountOfReinstatements]: {
            name: 'Number of Reinstatements'
        },
        [InputIds.Reinstatements.PaymentRule]: {
            name: 'Payment Rule'
        },
        [InputIds.Reinstatements.AmountPercent]: {
            name: 'Amount %'
        },
        [InputIds.Reinstatements.TimePercent]: {
            name: 'Time %'
        },
        [InputIds.Reinstatements.BrokerageType]: {
            name: 'Brokerage Type'
        },
        [InputIds.Reinstatements.BrokeragePercent]: {
            name: 'Brokerage %'
        }
    }
};

export default InputMetadata;
